import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const frontmatter = {
  "title": "Plausible Accessibility: Where AI Still Falls Short and Who to Call Instead",
  "slug": "plausible-accessibility",
  "engagement": "design-system-audit",
  "publishedAt": "2026-10-15T00:00:00.000Z",
  "readTime": "14 min read",
  "excerpt": "AI builds the most likely interface and accessibility lives in the specific one. Here is where that gap still fails disabled people and when to bring in a specialist or test with the people who use what you build.",
  "seoDescription": "AI builds the most likely interface, but accessibility lives in the specific one. Where that gap still fails disabled people and when to call a specialist.",
  "seoTitle": "Plausible Accessibility: Where AI Still Falls Short",
  "authorName": "Petri Lahdelma",
  "authorSlug": "petri-lahdelma",
  "mainImageUrl": "/blog/plausible-accessibility/hero-stencil-at-desk.webp"
};
export const whoToCallStages = [{
  id: "scoping",
  label: "Scoping",
  phrase: "scoping"
}, {
  id: "drafting",
  label: "Drafting",
  phrase: "drafting"
}, {
  id: "design-review",
  label: "Design review",
  phrase: "design review"
}, {
  id: "prototype",
  label: "Prototype",
  phrase: "prototype"
}, {
  id: "release-candidate",
  label: "Release candidate",
  phrase: "release candidate"
}, {
  id: "before-publishing",
  label: "Before publishing",
  phrase: "before publishing"
}, {
  id: "every-release",
  label: "Every release",
  phrase: "every release"
}, {
  id: "before-the-claim",
  label: "Before the claim",
  phrase: "before the claim"
}];
export const whoToCallPhases = [{
  label: "Before you build",
  stages: ["scoping", "drafting", "design-review", "prototype"]
}, {
  label: "Before you ship",
  stages: ["release-candidate", "before-publishing"]
}, {
  label: "Every release and every claim",
  stages: ["every-release", "before-the-claim"]
}];
export const whoToCall = [{
  id: "custom-widgets",
  title: "Custom widgets: date pickers, comboboxes, drag and drop, modals",
  tag: "design system team",
  lead: "A specialist and daily keyboard, switch, voice control and screen reader users",
  description: "Focus order and announcements only show up in operation. Give them the interaction pattern, a single-pointer alternative to dragging and the devices you support.",
  stages: ["release-candidate"]
}, {
  id: "forms-with-error-handling",
  title: "Forms with error handling",
  tag: "design system team",
  lead: "Screen reader, voice control and magnifier users",
  description: "Errors shown only in colour pass a structure check. Give them a task that triggers every error state.",
  stages: ["release-candidate"]
}, {
  id: "zoom-reflow-and-contrast-themes",
  title: "Zoom, reflow and contrast themes",
  tag: "design system team",
  lead: "A specialist and low-vision users who magnify or use high-contrast modes",
  description: "Tools render at one viewport and one colour scheme. Give them 400% zoom and 320px targets, forced-colours and high-contrast modes.",
  stages: ["every-release"]
}, {
  id: "motion-and-animation",
  title: "Motion and animation",
  tag: "design system team",
  lead: "A specialist and people with vestibular disorders",
  description: "Tools can't judge vestibular impact. Give them an inventory of animations and the reduced-motion behaviour.",
  stages: ["design-review"]
}, {
  id: "charts-maps-and-diagrams",
  title: "Charts, maps and diagrams",
  tag: "product team",
  lead: "A specialist and screen reader users from the first prototype",
  description: "Tools confirm alt text exists, not that it carries the point. Give them the chart's purpose in one sentence, the data and the palette.",
  stages: ["design-review"]
}, {
  id: "ai-chat-and-other-streaming-interfaces",
  title: "AI chat and other streaming interfaces",
  tag: "product team",
  lead: "A specialist to design the announcements, daily users of at least two screen readers and magnifier users",
  description: "Live regions behave differently with text that changes as it arrives. Give them tasks to complete, not screens to inspect.",
  stages: ["prototype", "release-candidate"]
}, {
  id: "login-signup-and-verification",
  title: "Login, signup and verification",
  tag: "product team",
  lead: "A specialist and people with memory and attention disabilities",
  description: "Unauthenticated scans stop at the login; cognitive tests need people. Give them test accounts, the two-factor path and the time limits.",
  stages: ["design-review", "release-candidate"]
}, {
  id: "native-mobile-apps",
  title: "Native mobile apps",
  tag: "product team",
  lead: "A mobile accessibility specialist and daily TalkBack and VoiceOver users",
  description: "Web scanners don't run there; platform settings vary. Give them builds, supported OS versions and text-size targets.",
  stages: ["release-candidate"]
}, {
  id: "captions-for-technical-legal-or-medical-media",
  title: "Captions for technical, legal or medical media",
  tag: "content team",
  lead: "A professional captioner and deaf and hard-of-hearing reviewers",
  description: "Accuracy scores ignore speaker changes, sounds and the terms that matter. Give them a glossary and speaker names.",
  stages: ["before-publishing"]
}, {
  id: "audio-description-for-video",
  title: "Audio description for video",
  tag: "content team",
  lead: "A describer and blind reviewers",
  description: "Only a person knows which visuals carry the meaning. Give them the script and the visuals that matter.",
  stages: ["before-publishing"]
}, {
  id: "content-in-sign-language",
  title: "Content in sign language",
  tag: "content team",
  lead: "Deaf signers and qualified interpreters (in Finland, through Kuurojen Liitto)",
  description: "Signed languages have their own grammar; avatars don't carry it. Give them which sign language, the source script and the budget.",
  stages: ["scoping"]
}, {
  id: "onboarding-and-plain-language-content",
  title: "Onboarding and plain-language content",
  tag: "content team",
  lead: "People with cognitive and learning disabilities and a plain-language editor (in Finland, Selkokeskus)",
  description: "Readability formulas reward short sentences, not clear ones. Give them the decisions the reader has to make.",
  stages: ["drafting"]
}, {
  id: "documents-and-pdfs",
  title: "Documents and PDFs",
  tag: "content team",
  lead: "A document remediation specialist and screen reader users",
  description: "Tags can exist and still be in the wrong reading order. Give them the source files, not only the exports.",
  stages: ["before-publishing"]
}, {
  id: "before-any-conformance-claim",
  title: "Before any conformance claim",
  tag: "leadership",
  lead: "An independent specialist running a WCAG-EM style evaluation",
  description: "A green report isn't a conformance evaluation. Give them the scope, the sample, the evidence you have and your accessibility statement.",
  stages: ["before-the-claim"]
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    figure: "figure",
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    ..._provideComponents(),
    ...props.components
  }, {ArticleFigure, StageList} = _components;
  if (!ArticleFigure) _missingMdxReference("ArticleFigure", true);
  if (!StageList) _missingMdxReference("StageList", true);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.p, {
      children: "Every code reviewer knows the shorthand. LGTM: looks good to me. It's one of the most common approvals in software and for most of what we review it's a reasonable bar."
    }), "\n", _jsx(_components.p, {
      children: "For accessibility it's an odd one. It names the one sense that many users and some reviewers don't use."
    }), "\n", _jsx(_components.p, {
      children: "I started thinking about the phrase in September, when I re-ran the accessibility checks behind every component in this site's design system. Each Storybook story in the test matrix writes a small evidence record: which checks ran, which passed, which runner produced them."
    }), "\n", _jsxs(_components.p, {
      children: ["The first run produced 949 records for the light theme. All green, every one with exit code zero and every one missing its axe result. I'd forgotten one environment variable, ", _jsx(_components.code, {
        children: "CI=1"
      }), ". Without it the test runner treated accessibility violations as warnings. The records came out without the check that mattered most. Nothing failed and nothing warned. It looked good to me."]
    }), "\n", _jsx(_components.p, {
      children: "I threw that run away. The name for what it produced stuck."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Plausible accessibility."
      })
    }), "\n", _jsx(_components.p, {
      children: "Plausible accessibility is output that has the shape of accessible work and passes for anyone who can't check it: a reviewer skimming a diff, a scanner that tests what it can measure, a model grading its own answer. Inaccessible work usually looks broken somewhere. Plausible work looks finished, so nobody goes back to check it until someone can't use it."
    }), "\n", _jsxs(ArticleFigure, {
      layout: "center",
      children: [_jsx(_components.figure, {
        "data-rehype-pretty-code-figure": "",
        children: _jsx(_components.pre, {
          tabIndex: "0",
          "data-language": "json",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          children: _jsxs(_components.code, {
            "data-language": "json",
            "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
            style: {
              display: "grid"
            },
            children: [_jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "{"
              })
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"storyId\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#032563",
                  "--shiki-dark": "#A5D6FF",
                  "--shiki-hcw": "#032563",
                  "--shiki-hcb": "#ADDCFF"
                },
                children: "\"navigation-languageswitcher-default\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"mode\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#032563",
                  "--shiki-dark": "#A5D6FF",
                  "--shiki-hcw": "#032563",
                  "--shiki-hcb": "#ADDCFF"
                },
                children: "\"light\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"sourceSHA\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#032563",
                  "--shiki-dark": "#A5D6FF",
                  "--shiki-hcw": "#032563",
                  "--shiki-hcb": "#ADDCFF"
                },
                children: "\"ab85ef6ed9a545a5e1b91a5ef4f7d6af1097c3f5\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"capturedAt\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#032563",
                  "--shiki-dark": "#A5D6FF",
                  "--shiki-hcw": "#032563",
                  "--shiki-hcb": "#ADDCFF"
                },
                children: "\"2026-10-15T08:36:30.037Z\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"runner\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#032563",
                  "--shiki-dark": "#A5D6FF",
                  "--shiki-hcw": "#032563",
                  "--shiki-hcb": "#ADDCFF"
                },
                children: "\"local-2026-10-15\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "  \"checks\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": {"
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "    \"accessibility-tree\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": {"
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "      \"passed\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#023B95",
                  "--shiki-dark": "#79C0FF",
                  "--shiki-hcw": "#023B95",
                  "--shiki-hcb": "#91CBFF"
                },
                children: "true"
              })]
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "    },"
              })
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "    \"axe-no-violations\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": {"
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "      \"passed\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#023B95",
                  "--shiki-dark": "#79C0FF",
                  "--shiki-hcw": "#023B95",
                  "--shiki-hcb": "#91CBFF"
                },
                children: "true"
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ","
              })]
            }), "\n", _jsxs(_components.span, {
              "data-line": "",
              children: [_jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "      \"axeViolations\""
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: ": "
              }), _jsx(_components.span, {
                style: {
                  "--shiki-light": "#023B95",
                  "--shiki-dark": "#79C0FF",
                  "--shiki-hcw": "#023B95",
                  "--shiki-hcb": "#91CBFF"
                },
                children: "0"
              })]
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "    }"
              })
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "  }"
              })
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "}"
              })
            })]
          })
        })
      }), _jsxs("figcaption", {
        children: ["One evidence record as committed: the story, the theme, the commit it was captured against and each check that ran. In September a missing ", _jsx("code", {
          children: "CI=1"
        }), " produced 949 of these with no ", _jsx("code", {
          children: "axe-no-violations"
        }), " entry and every run exited 0. Those records were thrown away before commit, so this is the healthy version."]
      })]
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-hollow-checkmark.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.h2, {
      children: "The median of an inaccessible web"
    }), "\n", _jsx(_components.p, {
      children: "AI is about to produce a great deal more of it. A language model writes the most likely continuation of what it has seen. For interface code, what it has seen is the public web."
    }), "\n", _jsxs(_components.p, {
      children: ["The web isn't a good teacher. WebAIM's annual scan of the top million home pages found detectable WCAG failures on ", _jsx(_components.a, {
        href: "https://webaim.org/projects/million/",
        children: "95.9% of them in the WebAIM Million 2026 report"
      }), ", up from 94.8% the year before. It was the first rise in six years. The average page had 56.1 detected errors. The report names AI-assisted \"vibe coding\" as one likely reason pages are getting more complex. Those are only the errors software can detect."]
    }), "\n", _jsxs(_components.p, {
      children: ["So when a code model writes a button, it writes the web's average button and the web's average button fails. Michael Fairchild at Microsoft built a benchmark to measure exactly this: 1,280 samples across eight frontier models, with no accessibility instructions. In ", _jsx(_components.a, {
        href: "https://dev.to/mfairchild365/ai-generated-accessibility-an-update-frontier-models-still-fail-but-skills-change-the-game-5629",
        children: "his May 2026 update"
      }), " the average pass rate was 12%. The best model passed a quarter of the time. The hardest prompt, a dark-themed shopping home page, never passed."]
    }), "\n", _jsxs(_components.p, {
      children: ["The obvious fix is to ask for accessibility. It helps, sometimes. Fairchild found that a one-line instruction took the pass rate from 12% to 37% and a short instruction file took it to 60%. A study of six AI design tools presented at W4A 2026, a web accessibility research conference, found the opposite: when designers ", _jsx(_components.a, {
        href: "https://doi.org/10.1145/3800424.3800430",
        children: "added accessibility requirements to their prompts, compliance went down"
      }), ". The designers judged that 39% of the AI tools' violations would need a major redesign, against 22% for their own work."]
    }), "\n", _jsx(_components.p, {
      children: "Different tools, measured differently. What they share is a ceiling. Fairchild's passes are automated passes and Microsoft's own report says a perfect score doesn't mean a page conforms to WCAG."
    }), "\n", _jsxs(_components.p, {
      children: ["Then there's the confidence. Eeva-Jonna Panula, known as Eevis, an Android accessibility specialist in Helsinki, asked an assistant to build an app ", _jsx(_components.a, {
        href: "https://dev.to/eevajonnapanula/does-claude-generate-accessible-apps-5759",
        children: "and explicitly asked for accessibility"
      }), ". The reply listed fifteen accessibility features it had considered. In the code she found a helper file nothing used, a semantic role that doesn't exist in the framework and cards with a redundant \"Tap for details\" on elements TalkBack, Android's screen reader, already announces as clickable. The list was plausible. The app wasn't."]
    }), "\n", _jsxs(_components.p, {
      children: ["People who use assistive technology meet this from the other side. When sixteen blind and low-vision programmers ", _jsx(_components.a, {
        href: "https://arxiv.org/abs/2506.13270",
        children: "used Copilot for two weeks"
      }), ", they worked faster and they also struggled to tell the tool what they meant, to check what it produced and to keep track of what it changed."]
    }), "\n", _jsxs(_components.p, {
      children: ["At scale, plausibility is sold by subscription. Overlays promise to make a site accessible with one script tag and disabled people have ", _jsx(_components.a, {
        href: "https://overlayfactsheet.com/",
        children: "organised against them for years"
      }), " because overlays get in the way of the screen readers and settings people already rely on. In April 2025 the US Federal Trade Commission ", _jsx(_components.a, {
        href: "https://www.ftc.gov/news-events/news/press-releases/2025/04/ftc-approves-final-order-requiring-accessibe-pay-1-million",
        children: "ordered accessiBe to pay $1 million"
      }), " and barred it from claiming its product makes sites WCAG-compliant without evidence. A small florist that installed another overlay still had a site a blind customer couldn't use, was sued and is now ", _jsx(_components.a, {
        href: "https://www.lflegal.com/2025/02/userway-overlay-lawsuit",
        children: "suing the overlay vendor"
      }), ". The florist bought the thing that promised to make the problem go away. The customer was the one locked out."]
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-median-web.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.h2, {
      children: "What the green runs hid"
    }), "\n", _jsxs(_components.p, {
      children: ["Once ", _jsx(_components.code, {
        children: "CI=1"
      }), " was back, the September runs went green for real. Green turned out to be a low bar. In the weeks that followed, this site turned up three more problems that no gate in the repo had caught."]
    }), "\n", _jsxs(_components.p, {
      children: ["The W3C saw this coming. Its guide to evaluation tools says ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/test-evaluate/tools/selecting/",
        children: "tools can not determine accessibility, they can only assist"
      }), ". WCAG-EM 2.0, its newest evaluation methodology, notes that ", _jsx(_components.a, {
        href: "https://www.w3.org/TR/wcag-em-2/",
        children: "most accessibility checks are not fully automatable"
      }), ". Even a passing automated rule only means no failure was detected; a pass ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/standards-guidelines/act/rules/about/",
        children: "typically requires further verification by human testers"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "The failures cluster in five places, each a judgment that needs context the model never sees: what something is for, how it sounds, the order it happens in, what it's called and how much effort it costs."
    }), "\n", _jsx(_components.h3, {
      children: "What the picture is for"
    }), "\n", _jsxs(_components.p, {
      children: ["WCAG's first success criterion, ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/non-text-content.html",
        children: "1.1.1 Non-text Content"
      }), ", asks for a text alternative that serves the equivalent purpose. Purpose is the hard part. The W3C images tutorial says the right alternative is ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/tutorials/images/",
        children: "determined by the author, depending on the usage, context and content"
      }), ". A vision model describes what's in the picture. It can't know why the picture is on the page."]
    }), "\n", _jsxs(_components.p, {
      children: ["A house icon that links to the homepage should be announced as \"Home\" and a model will describe it, accurately, as a house. A bar chart's meaning lives in its trend. The W3C's draft research on ", _jsx(_components.a, {
        href: "https://w3c.github.io/ai-accessibility/",
        children: "machine learning and accessibility"
      }), " notes that automatic descriptions often miss the values that matter."]
    }), "\n", _jsxs(_components.p, {
      children: ["Generative descriptions add detail, but the same draft observes that they change each time the same image is processed. Regenerate the alt text for a chart three times and you can get three different accounts of what it says. Pick your favourite. For a chart the better fix is usually a short written summary and a data table beside it, as the ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/tutorials/images/complex/",
        children: "W3C complex images tutorial"
      }), " has recommended for years."]
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-outline-pulled-loose.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.h3, {
      children: "Captions that score well"
    }), "\n", _jsxs(_components.p, {
      children: ["Captions look like the most solved problem on this list. The W3C draft puts automatic speech recognition at roughly 85% accuracy and notes that the Deaf community still considers it lacking. ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/captions-prerecorded.html",
        children: "1.2.2 Captions (Prerecorded)"
      }), " (a Level A criterion) and 1.2.4 for live content also ask for things accuracy scores ignore: who is speaking and the sounds that carry meaning."]
    }), "\n", _jsxs(_components.p, {
      children: ["Drishti Bansal, a deaf student who uses captions every day, ", _jsx(_components.a, {
        href: "https://medium.com/@dribans/how-ai-actually-helps-me-as-a-deaf-person-and-the-parts-no-one-talks-about-b7be5f9236c6",
        children: "describes where they break"
      }), ": accents, quiet voices, an air conditioner in the room, technical terms, two people talking at once. Tone disappears entirely."]
    }), "\n", _jsxs(_components.p, {
      children: ["A September 2026 preprint surveying 216 viewers, most of them deaf or hard of hearing, found the standard accuracy metrics ", _jsx(_components.a, {
        href: "https://arxiv.org/abs/2609.11408",
        children: "tracked viewers' ratings far worse for AI captions"
      }), " than for broadcast ones. The number on a vendor's slide and the audience are scoring different things. Bansal's own verdict is the better summary: \"AI makes communication possible, not equal.\""]
    }), "\n", _jsxs(_components.p, {
      children: ["Sign language is further away still. Signed languages have their own grammar and the World Federation of the Deaf and the interpreters' association WASLI have ", _jsx(_components.a, {
        href: "https://wfdeaf.org/wp-content/uploads/WFD-and-WASLI-Statement-on-Avatar-FINAL-14032018-Updated-14042018.pdf",
        children: "cautioned against signing avatars as a replacement for human interpreters"
      }), "."]
    }), "\n", _jsx(_components.h3, {
      children: "Order, focus and the keyboard"
    }), "\n", _jsxs(_components.p, {
      children: ["Generated components usually look fine. They fail when someone operates them with a keyboard. On the Frontend Masters blog, Durgesh Rajubhai Pawar walked through ", _jsx(_components.a, {
        href: "https://blog.master.dev/ai-generated-ui-is-inaccessible-by-default/",
        children: "a typical AI-generated sidebar"
      }), " that rendered perfectly and showed up in the accessibility tree as an anonymous generic container: no landmark, no heading, nothing focusable. Ten failures in 29 lines, none of them visible on screen."]
    }), "\n", _jsxs(_components.p, {
      children: ["The modal is the classic case. One developer's AI-built dashboard ", _jsx(_components.a, {
        href: "https://dev.to/lixingliangsy/ai-generated-ui-is-shipping-faster-than-accessibility-can-catch-up-1o2i",
        children: "let focus escape the dialog"
      }), " to the page behind it. A QA team auditing a booking site found a modal that ", _jsx(_components.a, {
        href: "https://dev.to/tudorsss-betterqa/accessibility-testing-what-automated-scanners-keep-missing-2d44",
        children: "never returned focus to the button that opened it"
      }), ". Their scan found 14 issues; a manual pass found 47 more."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/focus-order.html",
        children: "2.4.3 Focus Order"
      }), " asks that the order preserve meaning and operability. The W3C's guidance says several different orders can pass. A rule engine can't pick the right one. Someone has to press Tab and pay attention."]
    }), "\n", _jsxs(_components.p, {
      children: ["AI products have the same problem. Mike Gower at IBM found a floating chat button that ", _jsx(_components.a, {
        href: "https://medium.com/design-ibm/catching-up-on-accessibility-with-ai-chat-1129be33c184",
        children: "looked about twelve tab stops away and took 85 presses to reach"
      }), ". Streamed answers ", _jsx(_components.a, {
        href: "https://medium.com/design-ibm/usable-ai-chat-46dedcca6a8f",
        children: "keep changing until they settle"
      }), ", which breaks live regions, the mechanism screen readers use to announce content that changes (", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/status-messages.html",
        children: "4.1.3 Status Messages"
      }), "). Gower calls the usual workaround feast or famine: a periodic \"still composing\", then the whole reply at once."]
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-focus-maze.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.h3, {
      children: "Names that sound right"
    }), "\n", _jsxs(_components.p, {
      children: ["The first of this site's three came from its own header. The language button shows \"EN\". With English already active, its accessible name began ", _jsx(_components.code, {
        children: "EN, EN"
      }), ", then a separator, then \"Switch to English. Show language options.\" It passed every automated check in the repo. It had a name and the name contained the visible label, so it satisfied ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/label-in-name.html",
        children: "2.5.3 Label in Name"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "A screen reader user would have heard a repeated code and an instruction to switch to the language they were already using. A human auditor could fail it under 2.4.6 Headings and Labels. No machine would. The cause was ordinary: the header handed the component an action phrase where its contract asked for the language's name and the component dutifully read both."
    }), "\n", _jsxs(_components.p, {
      children: ["Eevis found the same family of problem in ", _jsx(_components.a, {
        href: "https://dev.to/eevajonnapanula/does-ai-generate-accessible-android-apps-2gb5",
        children: "AI-generated Android apps from four different tools"
      }), ": an \"Add yarn\" button described as \"Add new yarn\", so some screen readers read both. A machine can confirm a name exists. Whether it makes sense, said aloud in that order, takes someone listening."]
    }), "\n", _jsxs(_components.p, {
      children: ["The fix took two rounds. The first stopped the repetition but kept \"Show language options\" in the name, so the name changed whenever the menu opened, duplicating what ", _jsx(_components.code, {
        children: "aria-expanded"
      }), " already says. A review caught that. The name is now just the label and the language. The test that keeps it that way is the part worth copying, because it asserts the exact accessible name in each state:"]
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "tsx",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "tsx",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "expect"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "(trigger)."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "toHaveAccessibleName"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"EN, English\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "await"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " user."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "click"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "(trigger);"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "expect"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "(trigger)."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "toHaveAccessibleName"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"EN, English\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "expect"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "(trigger)."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "toHaveAttribute"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"aria-expanded\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"true\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "expect"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "(screen."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "getByRole"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"button\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ", { name: "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"FI, Finnish\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " }))."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: "toHaveAttribute"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "  \"lang\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "  \"fi\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ","
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "A presence check is what let the bug through. An exact-name check fails the moment a name stops making sense. It still isn't the whole story: a screen reader adds the role and state. Each reader phrases them its own way, so someone still has to listen to the result."
    }), "\n", _jsxs(ArticleFigure, {
      layout: "center",
      children: [_jsx(_components.figure, {
        "data-rehype-pretty-code-figure": "",
        children: _jsx(_components.pre, {
          tabIndex: "0",
          "data-language": "diff",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          children: _jsxs(_components.code, {
            "data-language": "diff",
            "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
            style: {
              display: "grid"
            },
            children: [_jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#0E1116",
                  "--shiki-dark": "#E6EDF3",
                  "--shiki-hcw": "#0E1116",
                  "--shiki-hcb": "#F0F3F6"
                },
                children: "   - group \"Language\":"
              })
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#6E011A",
                  "--shiki-dark": "#FFA198",
                  "--shiki-hcw": "#6E011A",
                  "--shiki-hcb": "#FFB1AF"
                },
                children: "-    - button \"EN, EN — Switch to English. Show language options\": EN"
              })
            }), "\n", _jsx(_components.span, {
              "data-line": "",
              children: _jsx(_components.span, {
                style: {
                  "--shiki-light": "#024C1A",
                  "--shiki-dark": "#7EE787",
                  "--shiki-hcw": "#024C1A",
                  "--shiki-hcb": "#72F088"
                },
                children: "+    - button \"EN, English\": EN"
              })
            })]
          })
        })
      }), _jsx("figcaption", {
        children: "The header's language button in the committed accessibility-tree snapshot, before and after, with English active. Both versions passed every automated check in the repo."
      })]
    }), "\n", _jsx(_components.h3, {
      children: "Meaning, memory and effort"
    }), "\n", _jsxs(_components.p, {
      children: ["The quietest failures are cognitive. When researchers audited six AI-generated websites for ASSETS 2025, a leading accessibility research conference, ", _jsx(_components.a, {
        href: "https://doi.org/10.1145/3663547.3759755",
        children: "52.93% of the 308 errors they found were cognitive accessibility issues"
      }), " and most cognitive needs sit outside WCAG itself. The W3C's ", _jsx(_components.a, {
        href: "https://www.w3.org/TR/coga-usable/",
        children: "Making Content Usable for People with Cognitive and Learning Disabilities"
      }), " covers what WCAG doesn't."]
    }), "\n", _jsxs(_components.p, {
      children: ["Fluent text makes this worse, because it reads as if someone already simplified it. The AAA criterion ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/reading-level.html",
        children: "3.1.5 Reading Level"
      }), " can be approximated with a readability formula. The formula will happily approve a short sentence that lost the one condition a reader needed."]
    }), "\n", _jsxs(_components.p, {
      children: ["Login is the clearest case. The W3C's note on ", _jsx(_components.a, {
        href: "https://www.w3.org/TR/turingtest/",
        children: "the inaccessibility of CAPTCHA"
      }), " cites research in which software beat audio reCAPTCHA about 90% of the time, while blind, deafblind and cognitively disabled people remain locked out. The test meant to tell humans from machines mostly tells machines from disabled humans."]
    }), "\n", _jsxs(_components.p, {
      children: ["WCAG only partly covers it. ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/WCAG22/Understanding/accessible-authentication-minimum.html",
        children: "3.3.8 Accessible Authentication (Minimum)"
      }), ", at AA, removes memory and puzzle tests from login but still allows object-recognition CAPTCHAs; 3.3.9 at AAA closes that gap and 1.1.1 requires a CAPTCHA to offer alternatives in more than one sense. Checking any of it means watching people with memory or attention disabilities go through the whole flow."]
    }), "\n", _jsx(_components.h2, {
      children: "What the machines are good at"
    }), "\n", _jsx(_components.p, {
      children: "None of this means keeping AI out of accessibility work. The real question is which work to give it."
    }), "\n", _jsx(_components.p, {
      children: "Automation is excellent at the measurable. The same pipeline that once wrote empty records caught a real failure in September: in dark theme, the user's chat bubble on this site rendered body text at 1.82:1 against its blue background and axe flagged it on every chat message story."
    }), "\n", _jsx(_components.p, {
      children: "The same bubble hid the second of the three. Its list bullets were the exact colour of its background: type a list into the chat and they vanished, at a measured contrast of 1:1. 1:1 is the technical term for invisible. No axe rule checks marker colour, so axe caught 1.82:1 and couldn't see 1:1. The fix was one line of CSS. The check that now guards it is a Storybook play test that measures the computed marker colour against the bubble and fails below 4.5:1, since numbered markers are text."
    }), "\n", _jsx(_components.p, {
      children: "Models are useful when the job is structured: drafting alt text for a person to edit, turning success criteria into a test plan or reviewing their own output. Fairchild's best result came from a two-step \"generate, then review\" loop, which reached 86% at about five and a half times the input tokens. He also found that pasting the whole of WCAG into the instructions often made the code worse. Short and specific works better, something like this in the instruction file your coding agent reads:"
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "md",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "md",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#023B95",
                "--shiki-light-font-weight": "bold",
                "--shiki-dark": "#79C0FF",
                "--shiki-dark-font-weight": "bold",
                "--shiki-hcw": "#023B95",
                "--shiki-hcw-font-weight": "bold",
                "--shiki-hcb": "#91CBFF",
                "--shiki-hcb-font-weight": "bold"
              },
              children: "## Accessibility"
            })
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: " "
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Use native elements first: <button>, <a href>, <label>, <dialog>. No click handlers on <div>."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Every control has a visible label and its accessible name starts with that label."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Dialogs move focus in, keep it inside and return it to the trigger on close."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Errors are text linked to their field with aria-describedby, never colour alone."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Announce status changes in a polite live region instead of moving focus."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Respect prefers-reduced-motion and keep layouts working at 200% text zoom."
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#702C00",
                "--shiki-dark": "#FFA657",
                "--shiki-hcw": "#702C00",
                "--shiki-hcb": "#FFB757"
              },
              children: "-"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " Before replying, review your output against this list and fix what fails."
            })]
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "The last line is the review step. It costs tokens. It's cheaper than shipping the first draft."
    }), "\n", _jsx(_components.p, {
      children: "The division of labour I trust is plain. Machines propose and measure. People decide. The people who decide whether something works are the ones who use it. Once a model's output is accepted because it sounds right, the work has slipped back into plausibility."
    }), "\n", _jsx(_components.h2, {
      children: "Who to call and when"
    }), "\n", _jsxs(_components.p, {
      children: ["The W3C's evaluation guidance asks for ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/test-evaluate/combined-expertise/",
        children: "combined expertise"
      }), " (people who understand disability barriers, assistive technology and adaptive strategies) and for ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/test-evaluate/involving-users/",
        children: "involving users"
      }), ", because testing with people finds what a conformance review misses. Those are two roles and they often meet in one person: many of the best accessibility specialists are disabled themselves. A specialist tells you whether something meets the standard and why. The people who use assistive technology every day tell you whether it works. You usually need both: the specialist while you build; users from the first prototype onwards, not as a final sign-off."]
    }), "\n", _jsx(_components.p, {
      children: "The cases from this article are sorted below by when to make the call; each names the team that owns it. Every entry comes from a failure described above or from a gap an expert reviewer added."
    }), "\n", "\n", "\n", "\n", _jsx(StageList, {
      items: whoToCall,
      stages: whoToCallStages,
      phases: whoToCallPhases
    }), "\n", _jsxs(_components.p, {
      children: ["Three rules make these calls work. Give testers tasks, not pages: \"change your delivery address\" finds problems that \"review the settings page\" never will. Don't ", _jsx(_components.a, {
        href: "https://www.w3.org/WAI/test-evaluate/involving-users/",
        children: "generalise from one or two people"
      }), "; different disabilities and assistive technologies surface different problems."]
    }), "\n", _jsx(_components.p, {
      children: "And pay people, properly. Disabled testers are experts doing skilled work, so budget for them as you would any other specialist, including their preparation time. Recruit through disabled people's organisations or specialist panels, with sign-up and scheduling that work with assistive technology. Let people use their own devices and settings. Ask for informed consent and keep only the data you need, since disability data is special-category data under the GDPR. Credit people's contribution when they want it."
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-who-to-call.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.p, {
      children: "If you need to make the case to leadership in Europe, it isn't optional anymore. The European Accessibility Act has applied since June 2025 and EN 301 549 is the standard used to show conformance. A plausible accessibility statement is now a compliance risk as well as a user one."
    }), "\n", _jsx(_components.h2, {
      children: "Where my own checks still fall short"
    }), "\n", _jsx(_components.p, {
      children: "I'd like to say the pipeline that missed 949 axe results is fixed. In the narrow sense it is: I now know to set the variable. But no package script sets it for me and when I went back a month later to reproduce the failure for this article, I couldn't. The same command now records the axe result either way. I don't know exactly what changed, which is its own kind of lesson."
    }), "\n", _jsx(_components.p, {
      children: "The third failure came from recapturing evidence for the language button. Writing the evidence files made Storybook hot-reload the story under test, so one check hung for its full 60-second timeout instead of failing. It passed in 241 milliseconds without the update step. Automation that can't detect its own gaps tends to fail politely."
    }), "\n", _jsx(_components.p, {
      children: "A small guard closes the silent-skip class in any pipeline that writes records like these. It fails when a check you rely on is missing, as well as when it fails:"
    }), "\n", _jsx(_components.figure, {
      "data-rehype-pretty-code-figure": "",
      children: _jsx(_components.pre, {
        tabIndex: "0",
        "data-language": "js",
        "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
        children: _jsxs(_components.code, {
          "data-language": "js",
          "data-theme": "github-light-high-contrast github-dark-default github-light-high-contrast github-dark-high-contrast",
          style: {
            display: "grid"
          },
          children: [_jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "for"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " ("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "const"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#023B95",
                "--shiki-dark": "#79C0FF",
                "--shiki-hcw": "#023B95",
                "--shiki-hcb": "#91CBFF"
              },
              children: " record"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: " of"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " records) {"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "  if"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " (record.optedOut) "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "continue"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "; "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#66707B",
                "--shiki-dark": "#8B949E",
                "--shiki-hcw": "#66707B",
                "--shiki-hcb": "#BDC4CC"
              },
              children: "// stories that disable axe on purpose"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "  const"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#023B95",
                "--shiki-dark": "#79C0FF",
                "--shiki-hcw": "#023B95",
                "--shiki-hcb": "#91CBFF"
              },
              children: " axe"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: " ="
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " record.checks["
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "\"axe-no-violations\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "];"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "  if"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " ("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "!"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "axe) "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "throw"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: " new"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: " Error"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "`${"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "record"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "storyId"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "}: axe result missing`"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })]
          }), "\n", _jsxs(_components.span, {
            "data-line": "",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "  if"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: " ("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "!"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "axe.passed) "
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: "throw"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#A0111F",
                "--shiki-dark": "#FF7B72",
                "--shiki-hcw": "#A0111F",
                "--shiki-hcb": "#FF9492"
              },
              children: " new"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#622CBC",
                "--shiki-dark": "#D2A8FF",
                "--shiki-hcw": "#622CBC",
                "--shiki-hcb": "#DBB7FF"
              },
              children: " Error"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "("
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "`${"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "record"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "."
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "storyId"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#032563",
                "--shiki-dark": "#A5D6FF",
                "--shiki-hcw": "#032563",
                "--shiki-hcb": "#ADDCFF"
              },
              children: "}: axe violations`"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: ");"
            })]
          }), "\n", _jsx(_components.span, {
            "data-line": "",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "#0E1116",
                "--shiki-dark": "#E6EDF3",
                "--shiki-hcw": "#0E1116",
                "--shiki-hcb": "#F0F3F6"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.p, {
      children: "The accessibility-tree snapshots this design system commits for every story in its test matrix are good at one thing: catching structural change. They record roles, names and states. They don't record whether a name makes sense out loud, whether an announcement arrives at the right moment or whether a sequence of steps is tiring. Those are judgments. A snapshot of a bad judgment passes forever."
    }), "\n", _jsx(_components.p, {
      children: "During the Helsinki Design System work, I worked with two accessibility experts from Siteimprove. Both have visual disabilities. That engagement ended with the project. The design system behind this site hasn't had a review like it, which is the gap this article is about."
    }), "\n", _jsx(_components.h2, {
      children: "Works for whom"
    }), "\n", _jsx(_components.p, {
      children: "Looks good to me is still a fine approval for code. For accessibility, it answers the wrong question."
    }), "\n", _jsx(_components.p, {
      children: "Models will keep getting closer to likely. Getting from likely to right, for one particular person, is still work for people."
    }), "\n", _jsx(_components.p, {
      children: "The useful questions are smaller and harder: works for whom, on what, doing which task and who told you?"
    }), "\n", _jsx(ArticleFigure, {
      layout: "full",
      children: _jsx("img", {
        src: "/blog/plausible-accessibility/fig-placing-a-person.webp",
        alt: ""
      })
    }), "\n", _jsx(_components.p, {
      children: _jsxs(_components.em, {
        children: ["If your team is working out where it needs a specialist or testing with disabled people, ", _jsx(_components.a, {
          href: "/contact",
          children: "get in touch"
        }), "."]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
